/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login if not authenticated.
 * Optionally checks for specific roles.
 */

import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: ('superadmin' | 'admin' | 'user')[];
  requireMFA?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  requireMFA = false,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { user, isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const verify = async () => {
      if (!isAuthenticated) {
        await checkAuth();
      }
      setIsChecking(false);
    };
    verify();
  }, [isAuthenticated, checkAuth]);

  // Show loading state while checking auth
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.includes(user.role);

    // Allow superadmin to access everything
    if (!hasRequiredRole && user.role !== 'superadmin') {
      return <Navigate to="/formspace" replace />;
    }
  }

  // Check MFA requirement
  if (requireMFA && user.mfaEnabled && !user.mfaEnabled) {
    return <Navigate to="/mfa/verify" state={{ from: location }} replace />;
  }

  // Check if user needs to complete profile (except when already on complete-profile page)
  if (user.profileIncomplete && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  return <>{children}</>;
}

/**
 * Public Only Route
 *
 * For pages that should only be accessible when NOT authenticated
 * (e.g., login, register pages)
 */
interface PublicOnlyRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function PublicOnlyRoute({
  children,
  redirectTo = '/formspace',
}: PublicOnlyRouteProps) {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to admin if already authenticated
  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
